/**
 * Single source of truth for the image hosts and the widths/qualities that
 * next/image may request.
 *
 * Imported by both `next.config.js` (remotePatterns, deviceSizes, imageSizes)
 * and `src/pages/api/thumbor-sign.ts` (source allowlist, ALLOWED_WIDTHS), so
 * the two can no longer drift apart. A mismatch between them does not fail any
 * build or type check — it shows up as individual srcSet candidates 400-ing on
 * specific viewports only.
 *
 * The host lists themselves live in the environment, never in this file:
 * IMAGES_DOMAINS for everything that may be rendered, THUMBOR_BYPASS_DOMAINS
 * for the subset that skips Thumbor. Adding a CDN is a CI variable change, not
 * a deploy of new code.
 *
 * Plain JS on purpose: next.config.js cannot import TypeScript.
 */

export const DEVICE_SIZES = [
  320, 375, 414, 640, 768, 1024, 1280, 1440, 1920, 2560,
];
export const IMAGE_SIZES = [16, 32, 48, 64, 128, 256, 512];

// Every quality a component actually passes (90 is the MediaItem default, 100
// comes from HeroCanvas) plus 75, which next/image uses when no quality prop
// is set. Keeping this small keeps Thumbor's render space small.
export const ALLOWED_QUALITIES = [75, 90, 100];

// Rejects anything that is not a public DNS host pattern: bare IPs, localhost,
// *.local and entries carrying a scheme, port or path. Keeps a fat-fingered
// env var (or a copy-pasted full URL) from widening the allowlist to an
// internal address instead of silently never matching.
function isPublicHostPattern(host) {
  const bare = host.replace(/^\*\*?\./, "");
  if (/[:/\\@?#*]/.test(bare)) return false;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(bare)) return false;
  if (bare === "localhost" || bare.endsWith(".localhost")) return false;
  if (bare.endsWith(".local") || bare.endsWith(".internal")) return false;
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(bare);
}

// Lower-cased because URL.hostname always is. "**." (the spelling Next.js'
// remotePatterns docs use) is normalised to "*." so both syntaxes work in
// IMAGES_DOMAINS and THUMBOR_BYPASS_DOMAINS — remotePatterns treats "*." as a
// single label, but the signing route matches subdomains either way, so the
// stricter of the two spellings wins.
export function parseHostList(value) {
  return (
    value
      ?.split(",")
      .map((host) => host.trim().toLowerCase())
      .filter(Boolean)
      .filter(isPublicHostPattern)
      .map((host) => host.replace(/^\*\*\./, "*.")) || []
  );
}

// Hosts served as-is instead of through Thumbor: CDNs that already deliver
// pre-sized, pre-optimized images and reject Thumbor's re-fetch because it
// lacks their own signed query params (Instagram/Facebook, typically).
export function getThumborBypassHosts() {
  return parseHostList(process.env.THUMBOR_BYPASS_DOMAINS);
}

// Bypassed hosts are part of this list too: they are still rendered through
// <Image>, they just skip the Thumbor hop — so both the signing route and
// (for the DISABLE_THUMBOR fallback) remotePatterns have to allow them, even
// when THUMBOR_BYPASS_DOMAINS lists a host that IMAGES_DOMAINS does not.
export function getAllowedImageHosts() {
  return [
    ...new Set([
      ...parseHostList(process.env.IMAGES_DOMAINS),
      ...getThumborBypassHosts(),
    ]),
  ];
}
